import React from 'react';

// Core pages
import Categories  from '../pages/Categories';
import Products    from '../pages/Products';
import Inventory   from '../pages/Inventory';
import Sales       from '../pages/Sales';
import Rentals     from '../pages/Rentals';
import Orders      from '../pages/Orders';
import Customers   from '../pages/Customers';
import Expenses    from '../pages/Expenses';
import Bookings    from '../pages/Bookings';
import Invoices    from '../pages/Invoices';
import Payments    from '../pages/Payments';
import Returns     from '../pages/Returns';
import Staff       from '../pages/Staff';
import Reports     from '../pages/Reports';
import Locations   from '../pages/Locations';
import Approvals   from '../pages/Approvals';
import Shifts      from '../pages/Shifts';
import Analytics   from '../pages/Analytics';  // Forecast is now a tab here
import Campaigns   from '../pages/Campaigns';  // Dead Stock is now a target_type here
import Billing        from '../pages/Billing';
import BillingSuccess from '../pages/BillingSuccess';
import Pricing        from '../pages/Pricing';
import Settings    from '../pages/Settings';   // Audit Logs + Import/Export are tabs here
import MobilePOS   from '../pages/MobilePOS';
import ZReport     from '../pages/ZReport';
import Terms        from '../pages/Terms';
import RefundPolicy from '../pages/RefundPolicy';

// Dynamic dashboard — replaces all module-specific dashboards
import DynamicDashboard from '../components/dashboard/DynamicDashboard';

// Gate components
import { TierGate } from '../components/PrivateRoute';

/**
 * All routes shared across every store type.
 * Module-specific routes (dashboard etc.) are injected at the module level.
 *
 * Changes from previous version:
 *   - /forecast removed  → Analytics has a Forecast tab
 *   - /dead-stock-campaign removed → Campaigns handles it
 *   - /audit removed from sidebar → it's a Settings tab; route kept for direct links
 *   - /data-import removed from sidebar → it's a Settings tab; route kept for direct links
 *   - /maintenance kept; sidebar hides it for non-rental stores via storeTypes
 *   - /approvals wrapped in <TierGate tier="enterprise">
 */
export const commonRoutes = [
  // Dashboard — single dynamic dashboard for all roles and store types
  { path: '/dashboard', element: <DynamicDashboard /> },

  // Inventory group
  { path: '/categories', element: <Categories />,  permission: 'view_products',   moduleKey: 'categories' },
  { path: '/products',   element: <Products />,    permission: 'view_products',   moduleKey: 'products' },
  { path: '/inventory',  element: <Inventory />,   permission: 'view_inventory',  moduleKey: 'inventory' },
  { path: '/locations',  element: <Locations />,   permission: 'view_inventory',  moduleKey: 'locations' },
  { path: '/orders',     element: <Orders />,      permission: 'view_inventory',  moduleKey: 'orders' },

  // Operations group
  { path: '/sales',     element: <Sales />,     permission: 'view_sales',     moduleKey: 'sales' },
  { path: '/shifts',    element: <Shifts />,    permission: 'view_sales',     moduleKey: 'shifts' },
  { path: '/rentals',   element: <Rentals />,   permission: 'view_rentals',   moduleKey: 'rentals' },
  { path: '/bookings',  element: <Bookings />,  permission: 'view_rentals',   moduleKey: 'bookings' },
  { path: '/returns',   element: <Returns />,   permission: 'process_return', moduleKey: 'returns' },

  // Customers group
  { path: '/customers',  element: <Customers />, permission: 'view_customers', moduleKey: 'customers' },
  // Dead Stock Campaign merged into Campaigns (target_type=dead_stock inside the page)
  { path: '/campaigns',  element: <Campaigns />, permission: 'view_customers', moduleKey: 'campaigns' },

  // Finance group
  { path: '/invoices',  element: <Invoices />,  permission: 'view_financials', moduleKey: 'invoices' },
  { path: '/payments',  element: <Payments />,  permission: 'view_financials', moduleKey: 'payments' },
  { path: '/expenses',  element: <Expenses />,  permission: 'manage_expenses', moduleKey: 'expenses' },

  // Analytics group
  { path: '/reports',   element: <Reports />,   permission: 'view_reports', moduleKey: 'reports' },
  // Forecast and AI insights are tabs inside Analytics — no standalone /forecast route
  { path: '/analytics', element: <Analytics />, permission: 'view_reports', moduleKey: 'analytics' },

  // POS
  { path: '/pos',      element: <MobilePOS />, permission: 'process_sale', moduleKey: 'pos' },
  { path: '/z-report', element: <ZReport />,   permission: 'view_reports', moduleKey: 'shifts' },

  // Admin group
  { path: '/staff', element: <Staff />, permission: 'manage_staff', moduleKey: 'staff' },
  {
    // Approvals: Enterprise plan only
    path: '/approvals',
    element: (
      <TierGate tier="enterprise">
        <Approvals />
      </TierGate>
    ),
    permission: 'manage_staff',
    moduleKey: 'approvals',
  },

  // Settings — contains Audit Logs tab + Import/Export tab
  { path: '/settings', element: <Settings />, permission: 'manage_settings', moduleKey: 'settings' },
  { path: '/billing',         element: <Billing /> },
  { path: '/billing/success', element: <BillingSuccess /> },
  { path: '/pricing',         element: <Pricing /> },

  // Legal — public pages, no auth required
  { path: '/terms',         element: <Terms /> },
  { path: '/refund-policy', element: <RefundPolicy /> },
];
